import React, { useState } from "react";
import { authService } from "../../../services/api/auth.service";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react";
import SharedLayout from "./SharedLayout";
import { useAuth } from "@/hooks/useAuth";
import GoogleLoginButton from "./GoogleLoginButton";
import { authValidation } from "../../../utils/authValidation";
import PasswordStrengthMeter from "./PasswordStrengthMeter";

const RegisterPage = ({ onSwitchToLogin }) => {
  const navigate = useNavigate();
  const { login } = useAuth();

  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    mobileNo: "",
    password: "",
    acceptTerms: false,
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [errors, setErrors] = useState({});
  const [mobileVerificationNeeded, setMobileVerificationNeeded] = useState(false);
  const [otpInput, setOtpInput] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    let finalValue = type === "checkbox" ? checked : value;

    // Hard-locking mobile to numeric only and 10 digits
    if (name === "mobileNo") {
      finalValue = value.replace(/\D/g, "").slice(0, 10);
    }

    setFormData((prev) => ({
      ...prev,
      [name]: finalValue,
    }));

    // Real-time validation
    if (type !== "checkbox") {
      validateField(name, finalValue);
    }
  };

  const validateField = (name, value) => {
    let error = "";
    switch (name) {
      case "email":
        error = authValidation.validateEmail(value);
        break;
      case "mobileNo":
        error = authValidation.validateMobile(value);
        break;
      case "password":
        error = authValidation.validatePassword(value);
        break;
      case "fullName":
        error = authValidation.validateFullName(value);
        break;
      default:
        break;
    }
    setErrors((prev) => ({ ...prev, [name]: error }));
    return error;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Final validation check
    const nameErr = validateField("fullName", formData.fullName);
    const emailErr = validateField("email", formData.email);
    const mobileErr = validateField("mobileNo", formData.mobileNo);
    const passwordErr = validateField("password", formData.password);

    if (nameErr || emailErr || mobileErr || passwordErr) {
      toast.error("Please fix the errors in the form before submitting");
      return;
    }

    if (!formData.acceptTerms) {
      toast.error("You must accept the Terms & Conditions");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await authService.register(formData);
      setMobileVerificationNeeded(true);
      toast.info(
        "Registration initiated. Please verify your mobile number to continue"
      );
    } catch (error) {
      if (error.field && error.message) {
        // Backend specifically told us which field failed (email or mobileNo)
        setErrors((prev) => ({ ...prev, [error.field]: error.message }));
        toast.error(error.message);
      } else {
        const errorMessage = error.message || "Registration failed";
        setError(errorMessage);
        toast.error(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleMobileVerification = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const response = await authService.verifyMobile({
        mobileNo: formData.mobileNo,
        otp: otpInput,
      });

      if (response && response.status === "success") {
        if (response.data?.user && response.data?.token) {
          const userData = response.data.user;
          const token = response.data.token;

          const loginSuccess = login(userData, token);

          if (loginSuccess) {
            toast.success("Registration successful!");
            setTimeout(() => {
              const pendingReferralCode = localStorage.getItem("pendingReferralCode");

              if (pendingReferralCode && userData.role !== "admin" && userData.role !== "expert") {
                navigate("/referral");
              } else {
                switch (userData.role) {
                  case "admin":
                    navigate("/admindashboard");
                    break;
                  case "expert":
                    navigate("/expert-dashboard");
                    break;
                  default:
                    navigate("/dashboard");
                    break;
                }
              }
            }, 500);
          } else {
            toast.error(
              "Failed to set authentication data. Please try logging in."
            );
            setTimeout(() => {
              onSwitchToLogin();
            }, 2000);
          }
        } else {
          toast.success(
            "Mobile verified successfully! Redirecting to login..."
          );
          setTimeout(() => {
            onSwitchToLogin();
          }, 2000);
        }
      } else {
        toast.error(
          "Verification failed: " + (response?.message || "Unknown error")
        );
      }
    } catch (err) {
      setError(err.message || "Mobile verification failed");
      toast.error(err.message || "Mobile verification failed");
    } finally {
      setLoading(false);
    }
  };

  const handleResendOTP = async () => {
    setLoading(true);
    try {
      await authService.sendMobileOtp(formData.mobileNo);
      toast.success("OTP resent successfully!");
    } catch (error) {
      toast.error("Failed to resend OTP. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col lg:flex-row">
      <div className="w-full lg:w-1/2 min-h-[40vh] lg:min-h-screen">
        <SharedLayout />
      </div>

      <div className="w-full lg:w-1/2 flex items-center justify-center px-4 py-8 lg:py-16 min-h-[60vh] lg:min-h-screen">
        <div className="w-full max-w-md">
          <ToastContainer position="bottom-right" autoClose={3000} />

          {error && (
            <div className="bg-red-50 text-red-600 p-4 rounded-lg mb-6 text-sm font-medium border border-red-100">
              {error}
            </div>
          )}

          {!mobileVerificationNeeded ? (
            <div className="space-y-6">
              <div className="text-center mb-8">
                <h1 className="text-2xl md:text-3xl font-bold text-[#102147] mb-2 mt-3">
                  Create Account
                </h1>
                <p className="text-gray-600">Join InvestHive today</p>
              </div>

              <GoogleLoginButton />

              <div className="flex items-center my-6">
                <div className="flex-1 border-t border-gray-200"></div>
                <span className="px-4 text-sm text-gray-500 font-medium">OR</span>
                <div className="flex-1 border-t border-gray-200"></div>
              </div>

              <form onSubmit={handleSubmit} className="w-full space-y-5">
                <div>
                  <input
                    type="text"
                    name="fullName"
                    placeholder="Full Name"
                    value={formData.fullName}
                    onChange={handleInputChange}
                    required
                    className={`w-full px-4 py-3.5 rounded-lg border-2 focus:ring-4 transition-all duration-200 outline-none ${errors.fullName ? "border-red-400 focus:border-red-500 focus:ring-red-100" : "border-gray-200 focus:border-[#44D2F6] focus:ring-[#44D2F6]/20"
                      }`}
                  />
                  {errors.fullName && <p className="text-xs text-red-500 mt-1 ml-1 font-medium">{errors.fullName}</p>}
                </div>

                <div>
                  <input
                    type="email"
                    name="email"
                    placeholder="Email Address"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className={`w-full px-4 py-3.5 rounded-lg border-2 focus:ring-4 transition-all duration-200 outline-none ${errors.email ? "border-red-400 focus:border-red-500 focus:ring-red-100" : "border-gray-200 focus:border-[#44D2F6] focus:ring-[#44D2F6]/20"
                      }`}
                  />
                  {errors.email && <p className="text-xs text-red-500 mt-1 ml-1 font-medium">{errors.email}</p>}
                </div>

                <div>
                  <input
                    type="tel"
                    name="mobileNo"
                    placeholder="Mobile Number"
                    value={formData.mobileNo}
                    onChange={handleInputChange}
                    required
                    className={`w-full px-4 py-3.5 rounded-lg border-2 focus:ring-4 transition-all duration-200 outline-none ${errors.mobileNo ? "border-red-400 focus:border-red-500 focus:ring-red-100" : "border-gray-200 focus:border-[#44D2F6] focus:ring-[#44D2F6]/20"
                      }`}
                  />
                  {errors.mobileNo && <p className="text-xs text-red-500 mt-1 ml-1 font-medium">{errors.mobileNo}</p>}
                </div>

                <div className="space-y-1">
                  <div className="relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      name="password"
                      placeholder="Password"
                      value={formData.password}
                      onChange={handleInputChange}
                      required
                      className={`w-full px-4 py-3.5 rounded-lg border-2 focus:ring-4 transition-all duration-200 outline-none pr-12 ${errors.password ? "border-red-400 focus:border-red-500 focus:ring-red-100" : "border-gray-200 focus:border-[#44D2F6] focus:ring-[#44D2F6]/20"
                        }`}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 transition-colors focus:outline-none"
                    >
                      {showPassword ? (
                        <EyeOff className="h-5 w-5" />
                      ) : (
                        <Eye className="h-5 w-5" />
                      )}
                    </button>
                  </div>
                  {errors.password && <p className="text-xs text-red-500 mt-1 ml-1 font-medium">{errors.password}</p>}
                  <PasswordStrengthMeter password={formData.password} />
                </div>

                <label className="flex items-center gap-3 cursor-pointer group">
                  <div className="relative flex items-center">
                    <input
                      type="checkbox"
                      name="acceptTerms"
                      checked={formData.acceptTerms}
                      onChange={handleInputChange}
                      required
                      className="peer h-5 w-5 cursor-pointer appearance-none rounded border-2 border-gray-300 transition-all checked:border-[#44D2F6] checked:bg-[#44D2F6] hover:border-gray-400"
                    />
                    <svg
                      className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-white opacity-0 peer-checked:opacity-100 transition-opacity"
                      width="12"
                      height="12"
                      viewBox="0 0 12 12"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M10 3L4.5 8.5L2 6"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                  <span className="text-sm text-gray-600 group-hover:text-gray-800 transition-colors">
                    I accept the Terms & Conditions
                  </span>
                </label>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-3.5 bg-gradient-to-r from-[#44D2F6] to-[#0C7FA5] text-white rounded-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                >
                  {loading ? "Signing Up..." : "Sign Up"}
                </button>
              </form>

              <p className="text-center text-gray-600">
                Already have an account?{" "}
                <button
                  onClick={onSwitchToLogin}
                  className="text-[#44D2F6] hover:text-[#0C7FA5] font-semibold transition-colors"
                >
                  Sign In
                </button>
              </p>
            </div>
          ) : (
            <div className="space-y-6">
              <div className="text-center mb-8">
                <h1 className="text-3xl md:text-4xl font-bold text-[#102147] mb-2">
                  Verify Mobile
                </h1>
                <p className="text-gray-600">
                  Enter the 6-digit OTP sent to <br />
                  <span className="font-semibold text-[#102147]">{formData.mobileNo}</span>
                </p>
              </div>

              <form
                onSubmit={handleMobileVerification}
                className="w-full space-y-5"
              >
                <div>
                  <input
                    type="text"
                    value={otpInput}
                    onChange={(e) =>
                      setOtpInput(e.target.value.replace(/\D/g, ""))
                    }
                    maxLength="6"
                    required
                    placeholder="Enter 6-digit OTP"
                    className="w-full px-4 py-3.5 rounded-lg border-2 border-gray-200 focus:border-[#44D2F6] focus:ring-4 focus:ring-[#44D2F6]/20 transition-all duration-200 outline-none text-center text-lg tracking-widest"
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading || otpInput.length !== 6}
                  className="w-full py-3.5 bg-gradient-to-r from-[#44D2F6] to-[#0C7FA5] text-white rounded-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                >
                  {loading ? "Verifying..." : "Verify OTP"}
                </button>
              </form>

              <div className="text-center space-y-3">
                <button
                  onClick={handleResendOTP}
                  disabled={loading}
                  className="text-[#44D2F6] hover:text-[#0C7FA5] font-semibold transition-colors text-sm"
                >
                  Resend OTP
                </button>

                <div>
                  <span className="text-gray-600 text-sm">Already have an account? </span>
                  <button
                    onClick={onSwitchToLogin}
                    className="text-[#44D2F6] hover:text-[#0C7FA5] font-medium transition-colors text-sm"
                  >
                    Sign In
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;